import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import Switch from 'react-switch';
import { Button, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Center, Column, Row } from '~/commonComponents';
import { IToggleControls } from '~/hooks/useToggle';
import ArrowDown from '~/images/down-arrow.svg';
import ArrowUp from '~/images/up-arrow.svg';
import JobPostingPopup from '../shared/JobPostingPopup';

interface IFormContract {
  break: string;
  isMealBreakPaid: boolean;
}

interface IInternalFormSchema {
  breakIdx: number;
  isPaid: boolean;
}

interface IProps {
  isOpen: boolean;
  toggleControl: IToggleControls;
  onSubmit(formData: IFormContract): void;
}

const ARROW_VARIANTS = {
  show: { opacity: 1 },
  hide: { opacity: 0 },
};
const BREAK_TIMES = ['00', '15', '30', '45', '60', '90', '120', '180'];
const BREAK_TIMES_SIZE = BREAK_TIMES.length - 1;
const DEFAULT_MEAL_BREAK_INDEX = 2;

export const SelectMealBreakTimePopup = ({ isOpen, toggleControl, onSubmit }: IProps) => {
  const [form, setForm] = useState<IInternalFormSchema>({
    breakIdx: DEFAULT_MEAL_BREAK_INDEX,
    isPaid: false,
  });

  const hasNextOptions = useMemo(() => {
    if (form.breakIdx < 0 || form.breakIdx > BREAK_TIMES_SIZE) return false;
    return form.breakIdx < BREAK_TIMES_SIZE;
  }, [form.breakIdx]);

  const hasPrevOptions = useMemo(() => {
    if (form.breakIdx < 0 || form.breakIdx > BREAK_TIMES_SIZE) return false;
    return form.breakIdx > 0;
  }, [form.breakIdx]);

  const handleArrowDown = () => {
    if (!hasPrevOptions) {
      return;
    }

    setForm((prev) => ({ ...prev, breakIdx: prev.breakIdx - 1 }));
  };

  const handleArrowUp = () => {
    if (!hasNextOptions) {
      return;
    }

    setForm((prev) => ({ ...prev, breakIdx: prev.breakIdx + 1 }));
  };

  const handleSubmit = () => {
    onSubmit({
      break: BREAK_TIMES[form.breakIdx],
      isMealBreakPaid: form.isPaid,
    });

    toggleControl.setOff();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <JobPostingPopup closeModal={toggleControl.setOff}>
      <Typography
        as="span"
        bold
        color={colors.primary[500]}
        size="heading_3"
        style={{ marginBlock: 25 }}
      >
        Select Meal Break
      </Typography>
      <Column
        alignItems="center"
        backgroundColor={colors.neutral[50]}
        borderRadius={25}
        boxShadow="0px 2px 6px 2px rgba(0, 0, 0, 0.15)"
        paddingHorizontal={45}
        paddingVertical={25}
      >
        <motion.img
          initial={hasNextOptions ? 'show' : 'hide'}
          animate={hasNextOptions ? 'show' : 'hide'}
          variants={ARROW_VARIANTS}
          transition={{ duration: 0.3 }}
          src={ArrowUp}
          className="pressable-img"
          style={{ marginBottom: 12, cursor: 'pointer' }}
          onClick={handleArrowUp}
          alt="up_arrow"
        />

        <Row alignItems="center" position="relative">
          <Center
            backgroundColor={colors.primary[500]}
            borderRadius={17}
            boxShadow="0px 2.2px 3.6px 0px rgba(0, 0, 0, 0.35) inset"
            justifyContent="center"
            alignItems="center"
            width={136}
          >
            <motion.p
              key={form.breakIdx}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                fontFamily: 'Nunito',
                fontSize: 46,
                color: colors.shades[0],
                margin: 0,
              }}
            >
              {BREAK_TIMES[form.breakIdx]}
            </motion.p>
          </Center>
          <Typography
            as="span"
            bold
            color={colors.neutral[500]}
            size="body_large"
            style={{ left: 'calc(100% + 13px)', margin: 0, position: 'absolute' }}
          >
            min
          </Typography>
        </Row>

        <motion.img
          initial={hasPrevOptions ? 'show' : 'hide'}
          animate={hasPrevOptions ? 'show' : 'hide'}
          variants={ARROW_VARIANTS}
          transition={{ duration: 0.3 }}
          src={ArrowDown}
          className="pressable-img"
          style={{ marginTop: 12, cursor: 'pointer' }}
          onClick={handleArrowDown}
          alt="down_arrow"
        />

        <Row alignItems="center" columnGap={10} marginTop={16}>
          <Switch
            checked={form.isPaid}
            onChange={(value) => setForm((prev) => ({ ...prev, isPaid: value }))}
            onColor={colors.secondary[500]}
            onHandleColor={colors.shades[0]}
            handleDiameter={20}
            uncheckedIcon={false}
            checkedIcon={false}
            offColor={colors.neutral[100]}
            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
            height={25}
            width={50}
            className="react-switch"
            id="material-switch"
          />
          <Typography
            as="span"
            bold
            color={colors.neutral[500]}
            size="body_large"
            style={{ margin: 0 }}
          >
            Meal Break is Paid
          </Typography>
        </Row>
      </Column>
      <Center marginVertical={25}>
        <Button size="md" onClick={handleSubmit}>
          Confirm
        </Button>
      </Center>
    </JobPostingPopup>
  );
};
